import { useEffect, useState } from "react";
import { AdminLayout } from "@/components/AdminLayout";
import { Button } from "@/components/ui/button";
import { api, formatApiErrorDetail } from "@/lib/api";
import { CORRECTION_INBOX } from "@/constants/testIds";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { MessageSquareWarning, CheckCircle2 } from "lucide-react";

export default function CorrectionInbox() {
  const [requests, setRequests] = useState([]);
  const [filter, setFilter] = useState("pending");

  const load = () => {
    api.get("/data-correction-requests").then(({ data }) => setRequests(data));
  };

  useEffect(load, []);

  const resolve = async (employeeId, requestId) => {
    try {
      await api.patch(`/employees/${employeeId}/data-correction-requests/${requestId}`);
      toast.success("Solicitud marcada como resuelta");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const filtered = filter === "all" ? requests : requests.filter((r) => r.status === filter);
  const pendingCount = requests.filter((r) => r.status === "pending").length;

  return (
    <AdminLayout>
      <div data-testid={CORRECTION_INBOX.page} className="max-w-3xl">
        <h1 className="font-heading text-2xl text-charcoal mb-1">Correcciones de datos</h1>
        <p className="text-sm text-charcoal/60 mb-5">
          Solicitudes que los empleados enviaron desde "Mis datos" en su portal cuando encuentran información incorrecta en su expediente.
        </p>

        <div className="flex gap-2 mb-5">
          {[
            { key: "pending", label: `Pendientes (${pendingCount})` },
            { key: "resolved", label: "Resueltas" },
            { key: "all", label: "Todas" },
          ].map((f) => (
            <button
              key={f.key}
              data-testid={`${CORRECTION_INBOX.filterButtonPrefix}-${f.key}`}
              onClick={() => setFilter(f.key)}
              className={`text-xs font-medium px-3 py-1.5 rounded-full transition-colors ${
                filter === f.key ? "bg-petroleum text-white" : "bg-card text-charcoal/60 border border-petroleum/15"
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>

        {filtered.length === 0 && (
          <p data-testid={CORRECTION_INBOX.emptyState} className="text-sm text-charcoal/50 bg-card border border-petroleum/10 rounded-2xl p-6 text-center">
            No hay solicitudes {filter === "pending" ? "pendientes" : filter === "resolved" ? "resueltas" : ""} por ahora.
          </p>
        )}

        <div data-testid={CORRECTION_INBOX.list} className="space-y-2">
          {filtered.map((r) => (
            <div key={r.id} className="bg-card border border-petroleum/10 rounded-2xl p-4 flex justify-between items-start gap-4">
              <div>
                <Link to={`/admin/empleados/${r.employee_id}`} className="text-sm font-semibold text-petroleum hover:underline">
                  {r.employee_name}
                </Link>
                <p className="text-sm text-charcoal/70 mt-1">{r.message}</p>
                <p className="text-xs text-charcoal/40 mt-1.5">{new Date(r.created_at).toLocaleString("es-MX")}</p>
              </div>
              {r.status === "pending" ? (
                <Button
                  data-testid={`${CORRECTION_INBOX.resolveButtonPrefix}-${r.id}`}
                  size="sm" variant="outline"
                  onClick={() => resolve(r.employee_id, r.id)}
                  className="rounded-full border-amber-400 text-amber-800 h-7 text-xs shrink-0"
                >
                  <MessageSquareWarning className="w-3 h-3 mr-1" /> Marcar resuelta
                </Button>
              ) : (
                <span className="flex items-center gap-1 text-xs font-medium text-emerald-600 shrink-0">
                  <CheckCircle2 className="w-3.5 h-3.5" /> Resuelta
                </span>
              )}
            </div>
          ))}
        </div>
      </div>
    </AdminLayout>
  );
}
